import React, { useRef, useState } from 'react';
import {
    AlertCircle,
    ArrowDownToLine,
    Boxes,
    CheckCircle,
    Info,
    Loader,
    Zap
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { ItemSearchModal } from '../../../common/ItemSearchModal';
import { ScanTextInput } from '../../../common/inputs';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { warehouseTransferService } from '../../../../services/warehouseTransferService';
import { ItemsDto } from '../../../../types/items';
import { ConfirmLine, ConsolidationView, ExecutionResult } from './types';
import { PullInView } from './components/PullInView';
import { EmptyBinView } from './components/EmptyBinView';
import { GatherItemView } from './components/GatherItemView';
import { FreeUpView } from './components/FreeUpView';
import { ConfirmSheet } from './components/ConfirmSheet';
import { useCapacityUpdate } from './useCapacityUpdate';
import { useSubModuleView } from '../../../../hooks/useSubModuleView';

interface ConsolidationAppProps {
    onBack: () => void;
}

interface ScanMessage {
    text: string;
    type: 'success' | 'error' | 'info';
}

/**
 * Operatørmodul «Konsolidering» — skann-drevet hjem.
 *   - Skann LOKASJON → «Frigjør denne» (tøm en nesten-tom bin; ett trykk veksler
 *     til «Trekk inn hit» for å fylle binnen i stedet).
 *   - Skann/søk VARE → «Samle vare» (samle spredt saldo i én lokasjon).
 *   - «Frigjør nå» → topp-N nesten-tomme lokasjoner nær operatøren.
 *
 * Modulen BESTILLER bare: bekreftelsesarket oppretter en KONSOLIDERING-merket
 * utleggsordre via consolidationService.execute. Selve flyttingen gjennomføres
 * som egen handling i Plassering — først da bokføres saldo (StcTransaction/ItemBins).
 */
export const ConsolidationApp: React.FC<ConsolidationAppProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouse();

    const warehouseID = currentWarehouse || '1';
    const companyNo = user?.companyNo || '';

    const [view, setView] = useSubModuleView<ConsolidationView>('home', { landing: 'home' });
    const [scannedCode, setScannedCode] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const [message, setMessage] = useState<ScanMessage | null>(null);

    // Dagsteller (utleggsordrer opprettet) — lokal state, nullstilles ved re-mount av modulen
    const [ordersToday, setOrdersToday] = useState(0);

    // Sist skannede lokasjon → nearPrefix (to første tegn) for «Frigjør nå»
    const [lastScannedLocation, setLastScannedLocation] = useState<string | null>(null);

    // Aktiv kontekst for visning A og B
    const [pullInBin, setPullInBin] = useState<string | null>(null);
    const [gatherItem, setGatherItem] = useState<{ itemNo: string; description1?: string } | null>(null);

    // Bekreftelsesark + refresh av aktiv visning etter utførelse
    const [confirmLines, setConfirmLines] = useState<ConfirmLine[] | null>(null);
    const [refreshToken, setRefreshToken] = useState(0);

    const [showItemSearchModal, setShowItemSearchModal] = useState(false);

    const scanInputRef = useRef<HTMLInputElement>(null);

    // Felles «Oppdater kapasitet»-flyt (operatøren overstyrer Compass når det er plass).
    // En lagret kapasitet re-analyserer aktiv visning (refreshToken) slik at forslaget
    // umiddelbart reflekterer den nye plassen.
    const capacity = useCapacityUpdate({
        warehouseID,
        onSaved: () => {
            setRefreshToken((prev) => prev + 1);
            setMessage({
                text: t('warehouse.modules.consolidation.capacity.updated', {}, 'Kapasitet oppdatert — forslag oppdatert'),
                type: 'success'
            });
        },
        onError: (msg) => setMessage({ text: msg, type: 'error' })
    });

    const refocusScan = () => setTimeout(() => scanInputRef.current?.focus(), 100);

    const nearPrefix = lastScannedLocation ? lastScannedLocation.substring(0, 2) : null;

    const openGather = (itemNo: string, description1?: string) => {
        setGatherItem({ itemNo, description1 });
        setView('gatherItem');
    };

    const openPullIn = (binCode: string) => {
        setPullInBin(binCode);
        setView('pullIn');
    };

    // Samme bin brukes av både «Frigjør denne» (emptyBin) og «Trekk inn hit» (pullIn);
    // operatøren kan veksle retning med ett trykk inne i visningene.
    const openEmptyBin = (binCode: string) => {
        setPullInBin(binCode);
        setView('emptyBin');
    };

    /**
     * Kontekstuell tolkning av skann (samme mønster som WarehouseTransferApp):
     * prøv vare først → «Samle vare»; ellers lokasjon → «Trekk inn hit»; ellers feil.
     */
    const handleScanInput = async (value: string) => {
        if (!value.trim()) return;

        setIsScanning(true);
        setMessage(null);

        try {
            const itemValidation = await warehouseTransferService.validateItem(value);
            if (itemValidation.valid) {
                openGather(itemValidation.itemNo, itemValidation.description1);
                setMessage({
                    text: t('warehouse.modules.consolidation.scan.itemRecognized', { '0': itemValidation.itemNo }, `Vare ${itemValidation.itemNo} — viser samleforslag`),
                    type: 'success'
                });
                return;
            }

            const locationValidation = await warehouseTransferService.validateLocation(companyNo, value);
            if (locationValidation.valid) {
                setLastScannedLocation(locationValidation.locationName);
                // Skann av en lokasjon leder med «Frigjør denne» — den vanligste
                // operatør-intensjonen (rydde en nesten-tom bin). Ett trykk bytter til
                // «Trekk inn hit» hvis hen heller vil fylle binnen.
                openEmptyBin(locationValidation.locationName);
                setMessage({
                    text: t('warehouse.modules.consolidation.scan.locationRecognized', { '0': locationValidation.locationName }, `Lokasjon ${locationValidation.locationName} — viser frigjør-forslag`),
                    type: 'success'
                });
                return;
            }

            setMessage({
                text: t('warehouse.modules.consolidation.scan.notFound', { '0': value }, `"${value}" ble ikke funnet som vare eller lokasjon`),
                type: 'error'
            });
        } catch (error) {
            console.error('Feil ved validering av skann:', error);
            setMessage({
                text: t('warehouse.modules.consolidation.scan.error', {}, 'Feil ved validering av skann'),
                type: 'error'
            });
        } finally {
            setIsScanning(false);
            refocusScan();
        }
    };

    const handlePullInTab = () => {
        if (lastScannedLocation) {
            openPullIn(lastScannedLocation);
        } else {
            setMessage({
                text: t('warehouse.modules.consolidation.pullIn.scanFirst', {}, 'Skann mål-lokasjonen først'),
                type: 'info'
            });
            refocusScan();
        }
    };

    const handleItemSearchSelect = (item: ItemsDto) => {
        setShowItemSearchModal(false);
        openGather(item.itemNo, item.description1);
    };

    const handleExecuted = (result: ExecutionResult) => {
        setOrdersToday((prev) => prev + result.orderNos.length);
        setRefreshToken((prev) => prev + 1);

        if (result.allDone) {
            setConfirmLines(null);
            setMessage({
                text: t(
                    'warehouse.modules.consolidation.result.orderCreated',
                    { '0': result.orderNos.join(', '), '1': result.lineCount, '2': result.plannedFreedCount },
                    `Utleggsordre #${result.orderNos.join(', ')} opprettet (${result.lineCount} linjer, frigjør ${result.plannedFreedCount} lokasjoner) — utføres i Plassering`
                ),
                type: 'success'
            });
            refocusScan();
        }
        // Delvis feil: arket forblir åpent med feil per linje + «Prøv igjen»
    };

    const tabs: Array<{ key: ConsolidationView; label: string; icon: React.ReactNode; onClick: () => void }> = [
        {
            key: 'pullIn',
            label: t('warehouse.modules.consolidation.pullIn.cardTitle', {}, 'Trekk inn hit'),
            icon: <ArrowDownToLine className="h-5 w-5" />,
            onClick: handlePullInTab
        },
        {
            key: 'gatherItem',
            label: t('warehouse.modules.consolidation.gatherItem.cardTitle', {}, 'Samle vare'),
            icon: <Boxes className="h-5 w-5" />,
            onClick: () => setShowItemSearchModal(true)
        },
        {
            key: 'freeUp',
            label: t('warehouse.modules.consolidation.freeUp.cardTitle', {}, 'Frigjør nå'),
            icon: <Zap className="h-5 w-5" />,
            onClick: () => setView('freeUp')
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="px-3 sm:px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                            <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg" iconSize={20} label="" />
                            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                                {t('warehouse.modules.consolidation.title', {}, 'Konsolidering')}
                            </h1>
                        </div>
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                            {t('warehouse.modules.consolidation.result.ordersToday', { '0': ordersToday }, `Ordrer i dag: ${ordersToday}`)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="p-3 sm:p-4 max-w-4xl mx-auto space-y-3 sm:space-y-4">
                {/* Skannfelt — alltid tilgjengelig, kontekstuell tolkning */}
                <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
                    <div className="relative">
                        <ScanTextInput
                            ref={scanInputRef}
                            value={scannedCode}
                            onChange={(e) => setScannedCode(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && scannedCode.trim()) {
                                    handleScanInput(scannedCode.trim());
                                    setScannedCode('');
                                }
                            }}
                            placeholder={t('warehouse.modules.consolidation.scan.placeholder', {}, 'Skann lokasjon eller vare…')}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base min-h-[44px]"
                            disabled={isScanning || confirmLines !== null || showItemSearchModal}
                            autoFocus
                        />
                        {isScanning && (
                            <div className="absolute right-3 top-3">
                                <Loader className="animate-spin h-5 w-5 text-cyan-500" />
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        {t('warehouse.modules.consolidation.scan.hint', {}, 'Lokasjon → frigjør denne · Vare → samle vare')}
                    </p>

                    {message && (
                        <div
                            className={`mt-2 p-3 rounded flex items-start text-sm ${
                                message.type === 'success'
                                    ? 'bg-green-50 text-green-800'
                                    : message.type === 'error'
                                        ? 'bg-red-50 text-red-800'
                                        : 'bg-blue-50 text-blue-800'
                            }`}
                        >
                            {message.type === 'success' ? (
                                <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                            ) : message.type === 'error' ? (
                                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                            ) : (
                                <Info className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                            )}
                            <span className="break-words">{message.text}</span>
                        </div>
                    )}
                </div>

                {/* Hjem: tre store kort. Aktiv visning: kompakte faner for å bytte flyt. */}
                {view === 'home' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                            onClick={handlePullInTab}
                            className="bg-white rounded-xl p-5 shadow-sm border text-left hover:border-cyan-300 hover:shadow-md transition-all min-h-[120px] flex flex-col gap-2"
                        >
                            <ArrowDownToLine className="h-8 w-8 text-cyan-500" />
                            <span className="font-semibold text-gray-900">
                                {t('warehouse.modules.consolidation.pullIn.cardTitle', {}, 'Trekk inn hit')}
                            </span>
                            <span className="text-sm text-gray-500">
                                {t('warehouse.modules.consolidation.pullIn.cardDescription', {}, 'Skann mål-lokasjonen og hent varer inn dit')}
                            </span>
                        </button>
                        <button
                            onClick={() => setShowItemSearchModal(true)}
                            className="bg-white rounded-xl p-5 shadow-sm border text-left hover:border-cyan-300 hover:shadow-md transition-all min-h-[120px] flex flex-col gap-2"
                        >
                            <Boxes className="h-8 w-8 text-cyan-500" />
                            <span className="font-semibold text-gray-900">
                                {t('warehouse.modules.consolidation.gatherItem.cardTitle', {}, 'Samle vare')}
                            </span>
                            <span className="text-sm text-gray-500">
                                {t('warehouse.modules.consolidation.gatherItem.cardDescription', {}, 'Samle spredt saldo for én vare i én lokasjon')}
                            </span>
                        </button>
                        <button
                            onClick={() => setView('freeUp')}
                            className="bg-white rounded-xl p-5 shadow-sm border text-left hover:border-cyan-300 hover:shadow-md transition-all min-h-[120px] flex flex-col gap-2"
                        >
                            <Zap className="h-8 w-8 text-cyan-500" />
                            <span className="font-semibold text-gray-900">
                                {t('warehouse.modules.consolidation.freeUp.cardTitle', {}, 'Frigjør nå')}
                            </span>
                            <span className="text-sm text-gray-500">
                                {t('warehouse.modules.consolidation.freeUp.cardDescription', {}, 'Nesten-tomme lokasjoner — ett flytt frigjør én lokasjon')}
                            </span>
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setView('home')}
                            className="px-4 py-2 rounded-full text-sm font-medium min-h-[44px] bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            {t('warehouse.modules.consolidation.scan.home', {}, 'Oversikt')}
                        </button>
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={tab.onClick}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium min-h-[44px] transition-colors ${
                                    view === tab.key
                                        ? 'bg-cyan-500 text-white'
                                        : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {tab.icon}
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Aktiv visning */}
                {view === 'emptyBin' && pullInBin && (
                    <EmptyBinView
                        binCode={pullInBin}
                        warehouseID={warehouseID}
                        refreshToken={refreshToken}
                        onConfirm={(lines) => setConfirmLines(lines)}
                        onSwitchToPullIn={() => setView('pullIn')}
                        onUpdateCapacity={capacity.open}
                    />
                )}
                {view === 'pullIn' && pullInBin && (
                    <PullInView
                        binCode={pullInBin}
                        warehouseID={warehouseID}
                        refreshToken={refreshToken}
                        onConfirm={(lines) => setConfirmLines(lines)}
                        onSwitchToEmpty={() => setView('emptyBin')}
                        onUpdateCapacity={capacity.open}
                    />
                )}
                {view === 'gatherItem' && gatherItem && (
                    <GatherItemView
                        itemNo={gatherItem.itemNo}
                        description1={gatherItem.description1}
                        warehouseID={warehouseID}
                        refreshToken={refreshToken}
                        onConfirm={(lines) => setConfirmLines(lines)}
                        onUpdateCapacity={capacity.open}
                    />
                )}
                {view === 'freeUp' && (
                    <FreeUpView
                        warehouseID={warehouseID}
                        nearPrefix={nearPrefix}
                        refreshToken={refreshToken}
                        onPick={(line) => setConfirmLines([line])}
                        onUpdateCapacity={capacity.open}
                    />
                )}
            </div>

            {/* Bekreftelsesark (felles for alle tre visningene) */}
            {confirmLines !== null && (
                <ConfirmSheet
                    initialLines={confirmLines}
                    warehouseID={warehouseID}
                    companyNo={companyNo}
                    onClose={() => {
                        setConfirmLines(null);
                        refocusScan();
                    }}
                    onExecuted={handleExecuted}
                    onUpdateCapacity={capacity.open}
                />
            )}

            {/* Felles «Oppdater kapasitet»-modal (operatøren overstyrer Compass) */}
            {capacity.modal}

            {/* Vare-søk for «Samle vare» (trykk i stedet for skann) */}
            <ItemSearchModal
                isOpen={showItemSearchModal}
                onClose={() => setShowItemSearchModal(false)}
                onSelect={handleItemSearchSelect}
                title={t('warehouse.modules.consolidation.gatherItem.searchTitle', {}, 'Søk vare å samle')}
                fetchLocations={async (itemNo: string) => {
                    const locations = await warehouseTransferService.getLocationsForItem(itemNo);
                    return locations.map((loc) => ({
                        locationName: loc.locationName,
                        warehouseID: loc.warehouseID || '',
                        availableQuantity: loc.availableQuantity
                    }));
                }}
            />
        </div>
    );
};
